import { useState, useEffect, useRef } from 'react';
import { Consultant, ConsultantFormData } from '../models/consultant';
import { fetchConsultants, createConsultant, updateConsultant, deleteConsultant } from '../api/consultant';
import { useSelector } from 'react-redux';
import { getUserProfile } from '../configuration/login/loginSlice';
import { fetchUserId } from '../api/fetchUser';

export const useConsultants = () => {
  const userProfile = useSelector(getUserProfile);

  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(500);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const requestIdRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const [showForm, setShowForm] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState<Consultant | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [consultantToDelete, setConsultantToDelete] = useState<Consultant | null>(null);

  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  // Drag to scroll logic
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: any) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    }
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const loadConsultants = async (page: number = 0, append: boolean = false, search: string = searchTerm) => {
    const requestId = ++requestIdRef.current;
    const isInitialLoad = !append && !hasLoadedOnceRef.current;

    if (append) {
      setLoadingMore(true);
    } else if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);
    try {
      const userId = await fetchUserId(userProfile);
      if (!userId) {
        setUserHasAccess(false);
        return;
      }

      const authorizedUserIds = import.meta.env.VITE_BACKOFFICE_AUTHORIZED_USER_IDS?.split(',').map((id: string) => id.trim()) || [];
      const isAuthorized = authorizedUserIds.includes(userId);
      if (!isAuthorized) {
        setUserHasAccess(false);
        return;
      }

      setUserHasAccess(true);

      const data = await fetchConsultants({ page, limit: pageSize, search: search.trim() || undefined });

      if (requestId !== requestIdRef.current) return;

      setConsultants(prev => {
        const next = append ? [...prev, ...data.consultants] : data.consultants;
        setHasMoreData(next.length < data.totalElements);
        return next;
      });
      setTotalElements(data.totalElements);
      setCurrentPage(page);
      hasLoadedOnceRef.current = true;
    } catch (err) {
      setError('Erro ao carregar consultores');
      console.error('Error loading consultants:', err);
    } finally {
      if (requestId === requestIdRef.current) {
        if (append) {
          setLoadingMore(false);
        } else if (isInitialLoad) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    }
  };

  const loadMoreConsultants = async () => {
    if (!hasMoreData || loading || loadingMore || refreshing) return;
    await loadConsultants(currentPage + 1, true);
  };

  const addConsultant = async (data: ConsultantFormData): Promise<boolean> => {
    setError(null);

    try {
      const newConsultant = await createConsultant(data);
      if (newConsultant) {
        await loadConsultants(0);
        return true;
      }
      return false;
    } catch (err: any) {
      const apiMessage: string = err?.response?.data?.message || '';
      const isDnError = apiMessage.includes('DealershipEntity') || apiMessage.includes('Unable to find');
      const userMessage = isDnError
        ? 'DN não cadastrado. Revise o código informado.'
        : 'Erro ao criar consultor.';
      console.error('Error creating consultant:', err);
      throw new Error(userMessage);
    }
  };

  const editConsultant = async (id: string, data: ConsultantFormData): Promise<boolean> => {
    setError(null);

    try {
      const updatedConsultant = await updateConsultant(id, data);
      if (updatedConsultant) {
        setConsultants(consultants.map(consultant => consultant.id === id ? updatedConsultant : consultant));
        return true;
      }
      return false;
    } catch (err: any) {
      const apiMessage: string = err?.response?.data?.message || '';
      const isDnError = apiMessage.includes('DealershipEntity') || apiMessage.includes('Unable to find');
      const userMessage = isDnError
        ? 'DN não cadastrado. Revise o código informado.'
        : 'Erro ao atualizar consultor.';
      console.error('Error updating consultant:', err);
      throw new Error(userMessage);
    }
  };

  const removeConsultant = async (id: string): Promise<boolean> => {
    setError(null);
    setDeleteLoading(true);
    try {
      const success = await deleteConsultant(id);
      if (success) {
        setConsultants(prev => prev.filter(consultant => consultant.id !== id));
        setTotalElements(prev => Math.max(prev - 1, 0));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao excluir consultor');
      console.error('Error deleting consultant:', err);
      return false;
    } finally {
      setDeleteLoading(false);
    }
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    loadConsultants(0);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      loadConsultants(0, false, searchTerm);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const handleEdit = (consultant: Consultant) => {
    setEditingConsultant(consultant);
    setShowForm(true);
  };

  const handleDelete = (consultant: Consultant) => {
    setConsultantToDelete(consultant);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (consultantToDelete?.id) {
      await removeConsultant(consultantToDelete.id);
      setShowDeleteDialog(false);
      setConsultantToDelete(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingConsultant(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingConsultant(null);
  };

  return {
    consultants,
    loading,
    loadingMore,
    refreshing,
    deleteLoading,
    error,
    loadConsultants,
    loadMoreConsultants,
    addConsultant,
    editConsultant,
    removeConsultant,
    showForm,
    editingConsultant,
    showDeleteDialog,
    consultantToDelete,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    userHasAccess,
    currentPage,
    totalElements,
    hasMoreData,
    pageSize,
    searchTerm,
    setSearchTerm,
    scrollContainerRef,
    isDragging,
    handleMouseDown,
    handleMouseLeave,
    handleMouseUp,
    handleMouseMove,
  };
};

